/** The running-process screen and the process detail page.
 *
 * This is a viewer, not a controller. It polls state the worker already wrote
 * and renders the worker's own annotated preview. Closing it stops nothing;
 * that is the whole point of the architecture and the copy says so plainly.
 */

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AppManifest, Artifact, EventRow, ProcessSummary, RunDetail, api,
} from "../lib/api";
import { bytes, clockTime, duration, number, relative, runClockStart, runDuration, since } from "../lib/format";
import { useAsync, useInterval, useNow, usePolling } from "../lib/useLive";
import {
  ArtifactRow, ConfirmDeleteProcess, ErrorBanner, FlowStrip, Loading, Modal, PageHeader, StateBadge,
} from "../components/ui";
import { Icon } from "../components/icons";

const LIVE_STATES = ["PENDING", "STARTING", "RUNNING", "DEGRADED", "STOPPING"];

export function RunView() {
  const { id } = useParams<{ id: string }>();
  const [showPreview, setShowPreview] = useState(true);
  const [stopping, setStopping] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);

  const { data: run, error } = usePolling<RunDetail>(() => api.run(id!), 2000);
  const live = run ? LIVE_STATES.includes(run.state) : false;
  const now = useNow(live);

  const catalog = useAsync<AppManifest[]>(() => api.apps(), []);
  const { data: results } = usePolling(() => api.results(id!), 2500, live);
  const { data: events } = usePolling<EventRow[]>(() => api.runEvents(id!), 5000, eventsOpen);
  const artifacts = useAsync<Artifact[]>(() => api.artifacts(id!), [run?.state]);

  if (error && !run) return <div className="page"><ErrorBanner error={error} /></div>;
  if (!run) return <div className="page"><Loading rows={5} /></div>;

  const metrics = run.metrics ?? {};
  const perApp = metrics.per_app ?? {};
  const flowMetrics: Record<string, any> = { __source__: { fps: metrics.decoded_fps } };
  Object.entries(perApp).forEach(([nodeId, value]: [string, any]) => {
    flowMetrics[nodeId] = {
      fps: value.target_fps, rows: value.rows, state: value.state, error: value.error,
    };
  });

  const elapsed = runDuration(run, now);
  const clockStart = runClockStart(run);
  const heartbeatAge = since(run.last_heartbeat_at, now);

  const stop = async () => {
    setStopping(true);
    setConfirmStop(false);
    try {
      await api.stopRun(run.id, "stopped from the run screen");
    } finally {
      setStopping(false);
    }
  };

  return (
    <div className="page stack">
      <PageHeader
        title={
          <span className="row" style={{ gap: 10 }}>
            {run.process_name}
            <StateBadge state={run.state} reason={run.state_reason} />
          </span>
        }
        description={
          <>
            <span className="id">{run.id}</span>
            {run.worker_id && <> · worker <span className="id">{run.worker_id}</span></>}
            {run.restart_count > 0 && (
              <> · <span style={{ color: "var(--amber)" }}>recovered {run.restart_count}×</span></>
            )}
          </>
        }
        actions={
          <>
            {live && (
              <button className="btn danger" disabled={stopping} onClick={() => setConfirmStop(true)}>
                <Icon name="stop" size={13} />
                {stopping ? "Stopping…" : "Stop process"}
              </button>
            )}
            <Link className="btn ghost" to={`/processes/${run.process_id}`}>Open process</Link>
          </>
        }
      />

      {live && (
        <div className="notice">
          This process runs on the server. You can close this tab, sign out, or come back from another
          computer — it keeps going until you stop it.
        </div>
      )}

      {run.state === "DEGRADED" && (
        <div className="error-banner">
          {run.state_reason || "The source or worker dropped. HoloXI is reconnecting and keeping the run."}
        </div>
      )}

      {!live && run.finalization && run.finalization.ok === false && (
        <div className="error-banner">
          Finalisation reported problems: {(run.finalization.problems ?? []).join("; ")}
        </div>
      )}

      <div className="card">
        <FlowStrip
          graph={run.config_snapshot}
          apps={catalog.data ?? []}
          metrics={flowMetrics}
          live={live}
          sourceName={run.holopod_id ?? run.source_id ?? "Source"}
        />
      </div>

      <div className="grid cols-4">
        <div className="card stat-card">
          <div className="stat-head">
            <div className="stat-label">Runtime</div>
            <span className="stat-icon"><Icon name="clock" size={14} /></span>
          </div>
          <div className="stat-value mono" style={{ fontSize: 26 }}>{duration(elapsed)}</div>
          <div className="stat-sub">
            {clockStart ? `started ${clockTime(clockStart)}` : "not started"}
          </div>
        </div>
        <div className="card stat-card">
          <div className="stat-head">
            <div className="stat-label">Stream</div>
            <span className="stat-icon" style={{ color: live ? "var(--signal)" : undefined }}>
              <Icon name="events" size={14} />
            </span>
          </div>
          <div className="stat-value" style={{ fontSize: 26, color: live ? "var(--signal)" : undefined }}>
            {metrics.decoded_fps ? metrics.decoded_fps.toFixed(1) : "—"} <span style={{ fontSize: 14 }}>fps</span>
          </div>
          <div className="stat-sub">{number(metrics.dropped_frames)} dropped</div>
        </div>
        <div className="card stat-card">
          <div className="stat-head">
            <div className="stat-label">Results</div>
            <span className="stat-icon"><Icon name="data" size={14} /></span>
          </div>
          <div className="stat-value" style={{ fontSize: 26 }}>{number(metrics.rows_written)}</div>
          <div className="stat-sub">rows written</div>
        </div>
        <div className="card stat-card">
          <div className="stat-head">
            <div className="stat-label">Data generated</div>
            <span className="stat-icon"><Icon name="storage" size={14} /></span>
          </div>
          <div className="stat-value" style={{ fontSize: 26 }}>
            {bytes(Object.values(run.artifact_summary ?? {}).reduce((total, entry: any) => total + (entry.bytes ?? 0), 0))}
          </div>
          <div className="stat-sub">
            {Object.entries(run.artifact_summary ?? {}).map(([kind, entry]: [string, any]) => `${entry.count} ${kind}`).join(" · ") || "no artifacts yet"}
          </div>
        </div>
      </div>

      <div className="grid split">
        <div className="stack">
          {live && (
            <div className="stack" style={{ gap: 8 }}>
              <div className="row">
                <h2>Live preview</h2>
                <div className="spacer" />
                <button className="btn ghost sm" onClick={() => setShowPreview(!showPreview)}>
                  {showPreview ? "Hide preview" : "Show preview"}
                </button>
              </div>
              {showPreview ? (
                <>
                  <LivePreview runId={run.id} state={run.state} />
                  <p className="hint">
                    The preview is a visualisation of what the worker is already doing. Hiding it does
                    not change processing or recording.
                  </p>
                </>
              ) : (
                <div className="card"><p className="hint" style={{ margin: 0 }}>Preview hidden. Processing continues.</p></div>
              )}
            </div>
          )}

          <h2>Latest results</h2>
          <div className="card flush">
            {(results?.results.length ?? 0) === 0 ? (
              <div style={{ padding: 18 }}>
                <p className="hint" style={{ margin: 0 }}>
                  {live ? "Waiting for the first results…" : "This run wrote no rows to the database sink."}
                </p>
              </div>
            ) : (
              <table>
                <thead>
                  <tr><th>Time</th><th>App</th><th>Frame</th><th>Result</th></tr>
                </thead>
                <tbody>
                  {results!.results.slice().reverse().map((row) => (
                    <tr key={row.id}>
                      <td className="mono">{clockTime(row.timestamp)}</td>
                      <td><span className="tag">{row.app_id}</span></td>
                      <td className="mono">{row.frame_id}</td>
                      <td className="mono" style={{ color: "var(--text-muted)" }}>{summarise(row.payload)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="row">
            <h2>Artifacts</h2>
            <div className="spacer" />
            {live && <span className="hint">Files appear as each segment closes</span>}
          </div>
          {(() => {
            const list = artifacts.data ?? [];
            const images = list.filter((artifact) => artifact.kind === "image");
            const rest = list.filter((artifact) => artifact.kind !== "image");
            const datasetImages = images.filter((artifact) => artifact.app_id === "dataset_collector");
            const otherImages = images.filter((artifact) => artifact.app_id !== "dataset_collector");
            const datasetBytes = datasetImages.reduce((sum, artifact) => sum + artifact.size_bytes, 0);
            return (
              <div className="stack" style={{ gap: 10 }}>
                {datasetImages.length > 0 && (
                  <details className="card">
                    <summary className="card-title" style={{ cursor: "pointer" }}>
                      Dataset collector
                      <span className="hint" style={{ fontWeight: 400, marginLeft: 8 }}>
                        {datasetImages.length} image{datasetImages.length === 1 ? "" : "s"} · {bytes(datasetBytes)}
                      </span>
                    </summary>
                    <div className="stack" style={{ marginTop: 12, gap: 10 }}>
                      <div className="row">
                        <div className="spacer" />
                        <a
                          className="btn sm primary"
                          href={api.zipArtifacts(run.id, { kind: "image", appId: "dataset_collector" })}
                          download
                        >
                          Download zip
                        </a>
                      </div>
                      <div className="card flush" style={{ border: "1px solid var(--line)" }}>
                        <table>
                          <thead>
                            <tr><th>File</th><th>Size</th><th>Created</th><th /></tr>
                          </thead>
                          <tbody>
                            {datasetImages.map((artifact) => (
                              <tr key={artifact.id}>
                                <td className="mono">{artifact.filename}</td>
                                <td className="mono">{bytes(artifact.size_bytes)}</td>
                                <td style={{ color: "var(--text-muted)" }}>{relative(artifact.created_at)}</td>
                                <td style={{ textAlign: "right" }}>
                                  <a className="btn sm" href={`${api.base}${artifact.download_url}`} download>Download</a>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </details>
                )}

                <div className="card flush">
                  {[...rest, ...otherImages].length === 0 && datasetImages.length === 0 ? (
                    <div style={{ padding: 18 }}>
                      <p className="hint" style={{ margin: 0 }}>Nothing finalised yet.</p>
                    </div>
                  ) : [...rest, ...otherImages].length === 0 ? (
                    <div style={{ padding: 18 }}>
                      <p className="hint" style={{ margin: 0 }}>Other artifacts will appear here.</p>
                    </div>
                  ) : (
                    <table>
                      <thead>
                        <tr><th>Kind</th><th>File</th><th>Size</th><th>Rows</th><th>Created</th><th /></tr>
                      </thead>
                      <tbody>
                        {[...rest, ...otherImages].map((artifact) => (
                          <ArtifactRow key={artifact.id} artifact={artifact} />
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            );
          })()}
        </div>

        <div className="stack">
          <h2>Apps</h2>
          {run.apps.map((app) => {
            const appMetrics: any = perApp[app.node_id] ?? {};
            const failed = app.state === "FAILED";
            return (
              <div className="card stack" key={app.node_id} style={{ gap: 8 }}>
                <div className="row">
                  <span className={`led ${failed ? "failed" : live && app.state === "RUNNING" ? "running" : ""}`} />
                  <div className="card-title">{app.app_id}</div>
                  <div className="spacer" />
                  <span className="hint">{app.state.toLowerCase()}</span>
                </div>
                {failed && app.state_reason && (
                  <p className="hint" style={{ color: "var(--coral)", margin: 0 }}>{app.state_reason}</p>
                )}
                <div className="row" style={{ gap: 18 }}>
                  <div>
                    <div className="stat-label">Rate</div>
                    <div className="mono">{appMetrics.target_fps ? `${Number(appMetrics.target_fps).toFixed(1)} fps` : "—"}</div>
                  </div>
                  <div>
                    <div className="stat-label">Rows</div>
                    <div className="mono">{number(appMetrics.rows ?? app.rows_written)}</div>
                  </div>
                  <div>
                    <div className="stat-label">Latency</div>
                    <div className="mono">{appMetrics.latency_ms ? `${appMetrics.latency_ms} ms` : "—"}</div>
                  </div>
                </div>
                {(appMetrics.last_result || app.last_result) && (
                  <div className="mono" style={{ fontSize: 11, color: "var(--signal)" }}>
                    {summarise(appMetrics.last_result ?? app.last_result)}
                  </div>
                )}
                {appMetrics.health?.duration && (
                  <div className="mono" style={{ fontSize: 11 }}>
                    recording {appMetrics.health.duration} · {bytes(appMetrics.health.bytes ?? 0)}
                  </div>
                )}
              </div>
            );
          })}

          <details className="card">
            <summary className="card-title" style={{ cursor: "pointer" }}>Advanced metrics</summary>
            <table style={{ marginTop: 12 }}>
              <tbody>
                {[
                  ["Inference latency", metrics.inference_latency_ms ? `${metrics.inference_latency_ms} ms` : "—"],
                  ["Combined inference rate", metrics.inference_fps ? `${metrics.inference_fps} fps` : "—"],
                  ["Worker CPU seconds", number(metrics.cpu_percent, 1)],
                  ["Worker memory", metrics.ram_mb ? `${number(metrics.ram_mb)} MB` : "—"],
                  ["Last heartbeat", run.last_heartbeat_at ? `${Math.round(heartbeatAge)}s ago` : "—"],
                  ["Storage prefix", run.storage_prefix],
                  ["Process version", `v${run.process_version}`],
                  ["Sync session", run.sync_session_id ?? "—"],
                ].map(([label, value]) => (
                  <tr key={label as string}>
                    <td className="hint">{label}</td>
                    <td className="mono" style={{ textAlign: "right", wordBreak: "break-all" }}>{value as string}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>

          <details
            className="card"
            onToggle={(event) => setEventsOpen((event.currentTarget as HTMLDetailsElement).open)}
          >
            <summary className="card-title" style={{ cursor: "pointer" }}>
              Events
              {eventsOpen && events != null && (
                <span className="hint" style={{ fontWeight: 400, marginLeft: 8 }}>
                  {events.length}
                </span>
              )}
            </summary>
            <div className="log" style={{ marginTop: 12 }}>
              {events == null && <p className="hint" style={{ margin: 0 }}>Loading…</p>}
              {events != null && events.length === 0 && (
                <p className="hint" style={{ margin: 0 }}>No events.</p>
              )}
              {(events ?? []).map((event) => (
                <div className="log-line" key={event.id}>
                  <span className="log-time">{clockTime(event.timestamp)}</span>
                  <span className={`log-type ${event.severity}`}>{event.event_type}</span>
                  <span style={{ color: "var(--text-muted)" }}>{event.message}</span>
                </div>
              ))}
            </div>
          </details>
        </div>
      </div>

      {confirmStop && (
        <Modal title="Stop this process?" onClose={() => setConfirmStop(false)}>
          <p>
            HoloXI will stop accepting new frames, flush pending results, close the video files
            correctly, upload anything outstanding and write the run manifest. That takes a few
            seconds — the run shows as <span className="mono">STOPPING</span> until it is safely finished.
          </p>
          <div className="row" style={{ marginTop: 16 }}>
            <div className="spacer" />
            <button className="btn ghost" onClick={() => setConfirmStop(false)}>Keep running</button>
            <button className="btn danger" onClick={stop}>Stop process</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function summarise(payload: Record<string, unknown> | undefined): string {
  if (!payload) return "";
  const skip = new Set(["bbox", "centroid", "velocity", "top_k", "labels", "values", "shape"]);
  return Object.entries(payload)
    .filter(([key, value]) => !skip.has(key) && value !== null && value !== "")
    .slice(0, 4)
    .map(([key, value]) => `${key}=${typeof value === "number" ? Number(value.toFixed(3)) : value}`)
    .join("  ");
}

// ------------------------------------------------------- process detail

export function ProcessDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const process = useAsync<ProcessSummary>(() => api.process(id!), [id]);
  const { data: runsData } = usePolling(
    () => api.runs(`?process_id=${id}&limit=20`), 4000, true, [id]);
  const versions = useAsync(() => api.versions(id!), [id]);
  const catalog = useAsync<AppManifest[]>(() => api.apps(), []);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const hasLive = (runsData ?? []).some((run: { stopped_at?: string | null; started_at?: string | null }) =>
    !run.stopped_at && run.started_at);
  const now = useNow(hasLive || Boolean(process.data?.active_run));

  if (process.error) return <div className="page"><ErrorBanner error={process.error} /></div>;
  if (!process.data) return <div className="page"><Loading rows={4} /></div>;

  const data = process.data;
  const active = data.active_run;
  const runs = { data: runsData };

  return (
    <div className="page stack">
      <PageHeader
        title={
          <span className="row" style={{ gap: 10 }}>
            {data.name}
            {active && <StateBadge state={active.state} />}
          </span>
        }
        description={
          <>
            {data.description || "No description."}
            {data.tags.length > 0 && (
              <div className="row wrap" style={{ gap: 6, marginTop: 8 }}>
                {data.tags.map((tag) => <span className="tag" key={tag}>{tag}</span>)}
              </div>
            )}
          </>
        }
        actions={
          <>
            {active ? (
              <Link className="btn primary" to={`/runs/${active.id}`}>Open live run</Link>
            ) : (
              <button
                className="btn primary"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    const run = await api.start(data.id);
                    navigate(`/runs/${run.id}`);
                  } finally { setBusy(false); }
                }}
              >
                <Icon name="play" size={13} />
                {busy ? "Starting…" : "Start process"}
              </button>
            )}
            <Link className="btn" to={`/processes/${data.id}/edit`}><Icon name="edit" size={13} />Edit</Link>
            <button className="btn ghost" onClick={async () => {
              const copy = await api.duplicateProcess(data.id);
              navigate(`/processes/${copy.id}`);
            }}>
              <Icon name="copy" size={13} />
              Duplicate
            </button>
            <button className="btn ghost" style={{ color: "var(--coral)" }}
                    disabled={Boolean(active)}
                    title={active ? "Stop the run before deleting this process" : undefined}
                    onClick={() => setDeleting(true)}>
              <Icon name="trash" size={13} />
              Delete
            </button>
          </>
        }
      />

      {active && (
        <div className="notice">
          Running since {relative(active.started_at)}. Editing this process creates version{" "}
          {data.current_version + 1}; the running copy is unaffected and keeps version {active.process_version}.
        </div>
      )}

      <div className="card">
        <FlowStrip graph={data.graph} apps={catalog.data ?? []} sourceName={data.source_name ?? undefined} />
      </div>

      {deleting && (
        <ConfirmDeleteProcess
          name={data.name}
          versionCount={data.current_version}
          onClose={() => setDeleting(false)}
          onConfirm={async (purgeData) => {
            await api.deleteProcess(data.id, purgeData);
            navigate("/processes");
          }}
        />
      )}

      <div className="grid split">
        <div className="stack">
          <h2>Run history</h2>
          <div className="card flush">
            {(runs.data?.length ?? 0) === 0 ? (
              <div style={{ padding: 18 }}>
                <p className="hint" style={{ margin: 0 }}>This process has never run.</p>
              </div>
            ) : (
              <table>
                <thead>
                  <tr><th>Run</th><th>State</th><th>Started</th><th>Duration</th><th>Version</th></tr>
                </thead>
                <tbody>
                  {runs.data!.map((run) => (
                    <tr key={run.id} className="clickable" onClick={() => navigate(`/runs/${run.id}`)}>
                      <td className="mono">{run.id.replace("run_", "")}</td>
                      <td><StateBadge state={run.state} /></td>
                      <td style={{ color: "var(--text-muted)" }}>{relative(run.started_at)}</td>
                      <td className="mono">{duration(runDuration(run, now))}</td>
                      <td className="mono">v{run.process_version}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="stack">
          <h2>Versions</h2>
          <div className="card flush">
            <table>
              <tbody>
                {(versions.data ?? []).map((version: any) => (
                  <tr key={version.version}>
                    <td className="mono">v{version.version}</td>
                    <td className="hint">{version.note || "—"}</td>
                    <td style={{ textAlign: "right", color: "var(--text-muted)" }}>
                      {version.is_current ? <span className="tag">current</span> : relative(version.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Live preview.
 *
 * MJPEG first, because it is the cheapest way to show motion. If it does not
 * produce a frame quickly - some proxies buffer multipart responses, and some
 * browsers stall on them - fall back to polling single frames. If neither
 * yields anything, say so: a black rectangle that never explains itself is
 * worse than a sentence.
 */
function LivePreview({ runId, state }: { runId: string; state: string }) {
  const [mode, setMode] = useState<"stream" | "poll">("stream");
  const [frame, setFrame] = useState<string | null>(null);
  const [seen, setSeen] = useState(false);
  const [waited, setWaited] = useState(0);

  // Give the stream a few seconds; if nothing rendered, switch to polling.
  useInterval(() => {
    setWaited((value) => value + 1);
    if (!seen && mode === "stream" && waited >= 5) setMode("poll");
  }, seen ? null : 1000);

  useInterval(() => {
    if (mode !== "poll") return;
    const url = api.runFrameUrl(runId);
    fetch(url)
      .then((response) => (response.ok ? response.blob() : null))
      .then((blob) => {
        if (!blob) return;
        setFrame((previous) => {
          if (previous) URL.revokeObjectURL(previous);
          return URL.createObjectURL(blob);
        });
        setSeen(true);
      })
      .catch(() => undefined);
  // Keep the fallback close to the worker's 12 FPS preview. The endpoint
  // returns only the newest JPEG, so slow clients do not build a backlog.
  }, mode === "poll" ? 100 : null);

  const starting = ["PENDING", "STARTING"].includes(state);

  return (
    <div className="preview">
      {mode === "stream" ? (
        <img
          src={api.runPreviewUrl(runId)}
          alt="Live preview with AI overlays"
          onLoad={() => setSeen(true)}
          onError={() => setMode("poll")}
        />
      ) : frame ? (
        <img src={frame} alt="Live preview with AI overlays" />
      ) : null}

      {seen ? (
        <div className="preview-badge">
          <span className="led running" />
          live · overlays from the worker{mode === "poll" ? " · polled" : ""}
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: 24, color: "var(--text-muted)" }}>
          <div className="row" style={{ justifyContent: "center", gap: 8, marginBottom: 8 }}>
            <span className="led degraded" />
            <span style={{ fontSize: 13 }}>
              {starting ? "Waiting for a worker to pick this up…" : "No preview frame yet"}
            </span>
          </div>
          {!starting && waited > 8 && (
            <p className="hint" style={{ maxWidth: "44ch", margin: "0 auto" }}>
              The run is processing — results and artifacts are unaffected. A missing preview usually
              means the source has not delivered a frame yet, or the API cannot reach Redis to read
              what the worker published.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
